/**
 * Leads panel — a site's contact form enquiries.
 *
 * Failed and pending deliveries are surfaced first and called out, because
 * those are the ones that need someone to act. Everything else is history.
 */

import { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Spinner } from './ui';
import {
  getSubmissions,
  resendSubmission,
  type Submission,
  type DeliveryStatus,
} from '../api/leads';

interface LeadsPanelProps {
  websiteId: string;
}

const STATUS_STYLES: Record<DeliveryStatus, { variant: 'success' | 'warning' | 'error'; label: string }> = {
  sent: { variant: 'success', label: 'Delivered' },
  failed: { variant: 'error', label: 'Failed' },
  // Written at insert and replaced by the real outcome. Still pending means the
  // send never recorded a result — a worker that died mid-send leaves this.
  pending: { variant: 'warning', label: 'Not confirmed' },
};

function formatDate(value: string): string {
  const parsed = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function SubmissionRow({
  submission,
  onResent,
}: {
  submission: Submission;
  onResent: (updated: DeliveryStatus, error?: string | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const status = STATUS_STYLES[submission.delivery_status];
  const needsAttention = submission.delivery_status !== 'sent';

  async function handleResend() {
    setResending(true);
    setResendError(null);
    try {
      const res = await resendSubmission(submission.id);
      // Report what actually happened. A failed resend stays failed — telling
      // staff it worked when it did not is worse than telling them nothing.
      onResent(res.data.deliveryStatus, res.data.error ?? res.error ?? null);
      if (res.data.deliveryStatus !== 'sent') {
        setResendError(res.data.error || res.error || 'Delivery failed again.');
      }
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Could not resend.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className={`border rounded-lg p-4 ${needsAttention ? 'border-red-200 bg-red-50/40' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{submission.name}</span>
            <a href={`mailto:${submission.email}`} className="text-sm text-primary-600 hover:underline">
              {submission.email}
            </a>
            {submission.phone && <span className="text-sm text-gray-500">{submission.phone}</span>}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {formatDate(submission.created_at)}
            {submission.delivered_to && <> · sent to {submission.delivered_to}</>}
          </p>
        </div>
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>

      <p
        className={`text-sm text-gray-700 mt-3 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-3'}`}
      >
        {submission.message}
      </p>
      {submission.message.length > 200 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-primary-600 hover:underline mt-1"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}

      {needsAttention && (
        <div className="mt-3 pt-3 border-t border-red-200">
          {submission.delivery_error && (
            <p className="text-xs text-red-700 mb-2">
              <span className="font-medium">Delivery error:</span> {submission.delivery_error}
            </p>
          )}
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={handleResend} loading={resending} disabled={resending}>
              {resending ? 'Resending…' : 'Resend'}
            </Button>
            <span className="text-xs text-gray-500">
              The enquiry is safe — only the email failed.
            </span>
          </div>
          {resendError && <p className="text-xs text-red-700 mt-2">{resendError}</p>}
        </div>
      )}
    </div>
  );
}

export function LeadsPanel({ websiteId }: LeadsPanelProps) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [counts, setCounts] = useState({ total: 0, failed: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSubmissions(websiteId);
      setSubmissions(res.data.submissions);
      setCounts({ total: res.data.total, failed: res.data.failed, pending: res.data.pending });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load leads.');
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <p className="text-sm text-red-700">{error}</p>
        <Button size="sm" variant="secondary" className="mt-3" onClick={() => void load()}>
          Retry
        </Button>
      </Card>
    );
  }

  const needsAttention = counts.failed + counts.pending;

  return (
    <Card>
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-semibold text-gray-900">
          Leads{' '}
          <span className="text-sm font-normal text-gray-500">
            ({counts.total} {counts.total === 1 ? 'enquiry' : 'enquiries'})
          </span>
        </h3>
        {needsAttention > 0 && (
          <Badge variant="error">
            {needsAttention} need{needsAttention === 1 ? 's' : ''} attention
          </Badge>
        )}
      </div>

      {submissions.length === 0 ? (
        <p className="text-sm text-gray-500">
          No enquiries yet. Submissions from this site's contact form appear here, and are emailed
          to the site's contact recipient.
        </p>
      ) : (
        <div className="space-y-3">
          {submissions.map((s) => (
            <SubmissionRow
              key={s.id}
              submission={s}
              onResent={() => void load()}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
